/**
 * Dashboard plan status banner.
 *
 * Shown at the top of the dashboard for all authenticated users. Displays
 * the current plan tier, per-module remaining attempts (for limited tiers),
 * time remaining (for all paid passes), and an upgrade / renew CTA.
 *
 * Variants:
 *  - free      : amber — lists module restrictions + per-module attempts + upgrade CTA
 *  - starter   : sky/orange — per-module attempts + days remaining (+ renew CTA when urgent
 *                or a module is exhausted)
 *  - paid-unlimited (exam-ready / full-prep): emerald — days remaining; orange when < 7 days
 *  - expired   : destructive red — names the expired pass (from the backend's
 *                recent_expiry, sourced from tb_user_plan_history) + renew CTA;
 *                dismissible and shown until the user closes it (persisted per
 *                user + expiry timestamp in localStorage)
 *
 * Time display uses Intl.RelativeTimeFormat so it renders in the active UI
 * language (e.g. "in 5 days" / "in 5 Tagen") without extra translation keys.
 *
 * @module PlanBanner
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle2, Clock, X, Zap } from 'lucide-react';
import type { TFunction } from 'i18next';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { ROUTES } from '@/lib/routeConstants';
import { useAuth } from '@/hooks/useAuth';
import { useCurrentPlan } from '@/hooks/useCurrentPlan';
import { useEntitlement } from '@/hooks/useHumanReviews';
import type { CurrentPlan } from '@/lib/planApi';
import type { Entitlement } from '@/lib/humanReviewsApi';

/** localStorage key prefix for the per-user dismissed expiry timestamp. */
const EXPIRY_DISMISS_KEY_PREFIX = 'dg_plan_expiry_dismissed_';

// ── Constants ──────────────────────────────────────────────────────────────

/** Show urgent coloring when fewer than this many days remain on a paid pass. */
const URGENCY_DAYS = 7;

// ── Time helpers ──────────────────────────────────────────────────────────

function msUntilExpiry(expiresAt: string): number {
  return new Date(expiresAt).getTime() - Date.now();
}

/**
 * Returns a locale-aware "in X days / in X hours" string using the browser's
 * Intl.RelativeTimeFormat — no extra i18n keys needed for the time unit.
 */
function formatTimeRemaining(expiresAt: string, locale: string): string {
  const ms = msUntilExpiry(expiresAt);
  if (ms <= 0) return '';
  const totalHours = Math.floor(ms / (1000 * 60 * 60));
  const days = Math.floor(totalHours / 24);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'always' });
  if (days >= 1) return rtf.format(days, 'day');
  return rtf.format(Math.max(1, totalHours), 'hour');
}

// ── Module mini-card (reused by Free + Starter banners) ───────────────────

interface ModuleMiniCardProps {
  readonly module: string;
  readonly used: number;
  readonly quota: number;
  readonly t: TFunction;
}

function ModuleMiniCard({ module, used, quota, t }: ModuleMiniCardProps) {
  const remaining = Math.max(0, quota - used);
  const isExhausted = remaining === 0;
  const usedPct = Math.min(100, Math.round((used / quota) * 100));

  return (
    <div
      className={cn(
        'rounded border px-2.5 py-2 min-w-[7.5rem] flex-1',
        isExhausted
          ? 'border-destructive/40 bg-destructive/5'
          : 'border-border bg-background/70',
      )}
    >
      <div className="flex items-center justify-between gap-1 mb-1.5">
        <span
          className={cn(
            'font-ui text-xs font-medium capitalize truncate',
            isExhausted ? 'text-muted-foreground' : 'text-foreground',
          )}
        >
          {t(`moduleSelection.modules.${module}.label`, { defaultValue: module })}
        </span>
        <span
          className={cn(
            'font-ui text-xs font-semibold shrink-0',
            isExhausted ? 'text-destructive' : 'text-primary',
          )}
        >
          {isExhausted
            ? t('dashboard.planBanner.exhausted', { defaultValue: 'Used up' })
            : t('dashboard.planBanner.attemptsLeft', {
                defaultValue: '{{count}} left',
                count: remaining,
              })}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-all',
            isExhausted ? 'bg-destructive/60' : 'bg-primary',
          )}
          style={{ width: `${usedPct}%` }}
        />
      </div>
    </div>
  );
}

interface HumanReviewMiniCardProps {
  readonly entitlement: Entitlement;
  readonly t: TFunction;
}

function HumanReviewMiniCard({ entitlement, t }: HumanReviewMiniCardProps) {
  const included = Math.max(0, entitlement.includedCount);
  const consumed = Math.max(0, Math.min(entitlement.consumedCount, included));
  const remaining = Math.max(0, entitlement.remaining);
  const hasQuota = included > 0;
  const isExhausted = hasQuota && remaining === 0;
  const usedPct = hasQuota ? Math.min(100, Math.round((consumed / included) * 100)) : 0;

  return (
    <div
      className={cn(
        'rounded border px-2.5 py-2 min-w-[7.5rem] flex-1',
        isExhausted
          ? 'border-destructive/40 bg-destructive/5'
          : 'border-border bg-background/70',
      )}
    >
      <div className="flex items-center justify-between gap-1 mb-1.5">
        <span
          className={cn(
            'font-ui text-xs font-medium truncate',
            isExhausted ? 'text-muted-foreground' : 'text-foreground',
          )}
        >
          {t('dashboard.planBanner.humanReviewsCardLabel', {
            defaultValue: 'Human reviews',
          })}
        </span>
        <span
          className={cn(
            'font-ui text-xs font-semibold shrink-0',
            isExhausted ? 'text-destructive' : hasQuota ? 'text-primary' : 'text-muted-foreground',
          )}
        >
          {hasQuota
            ? isExhausted
              ? t('dashboard.planBanner.exhausted', { defaultValue: 'Used up' })
              : t('dashboard.planBanner.humanReviewsLeft', {
                  defaultValue: '{{count}} left',
                  count: remaining,
                })
            : t('dashboard.planBanner.humanReviewsNotIncluded', {
                defaultValue: 'Not included',
              })}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-all',
            isExhausted ? 'bg-destructive/60' : 'bg-primary',
          )}
          style={{ width: `${usedPct}%` }}
        />
      </div>
    </div>
  );
}

// ── Banner props type ─────────────────────────────────────────────────────

interface BannerProps {
  readonly plan: CurrentPlan;
  readonly t: TFunction;
  readonly locale: string;
  readonly onUpgrade: () => void;
  readonly entitlement: Entitlement | null;
}

// ── Free banner ───────────────────────────────────────────────────────────

function FreeBanner({ plan, t, entitlement, onUpgrade }: BannerProps) {
  const modules = plan.limits.allowed_modules ?? [];
  const quota = plan.mock_quota ?? 1;
  const moduleUsage = plan.module_usage ?? {};

  return (
    <div className="rounded-lg border-2 border-amber-400/50 bg-amber-50/80 p-4 space-y-3">
      {/* Header row */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex gap-3 items-start">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-400/20 text-amber-700">
            <Zap className="h-4 w-4" aria-hidden />
          </div>
          <div>
            <p className="font-ui font-semibold text-sm text-amber-900">
              {t('dashboard.planBanner.free.title', { defaultValue: 'Free Plan' })}
            </p>
            <p className="font-ui text-xs text-amber-800/80 mt-0.5">
              {t('dashboard.planBanner.free.limitNote', {
                defaultValue: 'Reading & Listening only · {{quota}} attempt per module',
                quota,
              })}
            </p>
          </div>
        </div>
        <Button
          type="button"
          size="sm"
          className="font-ui shrink-0 border-0 bg-amber-600 hover:bg-amber-700 text-white"
          onClick={onUpgrade}
        >
          {t('dashboard.planBanner.free.cta', { defaultValue: 'Upgrade plan' })}
        </Button>
      </div>

      {/* Per-module attempt cards */}
      <div className="space-y-1.5">
        <p className="font-ui text-xs font-medium uppercase tracking-wide text-amber-800/70">
          {t('dashboard.planBanner.moduleAttemptsTitle', { defaultValue: 'Attempts per module' })}
        </p>
        <div className="flex flex-wrap gap-2">
          {modules.map((module) => (
            <ModuleMiniCard
              key={module}
              module={module}
              used={moduleUsage[module] ?? 0}
              quota={quota}
              t={t}
            />
          ))}
        </div>
      </div>

      {entitlement && (
        <div className="space-y-1.5">
          <p className="font-ui text-xs font-medium uppercase tracking-wide text-amber-800/70">
            {t('dashboard.planBanner.humanReviewsTitle', { defaultValue: 'Human reviews' })}
          </p>
          <HumanReviewMiniCard entitlement={entitlement} t={t} />
        </div>
      )}

      {/* Upgrade hint */}
      <p className="font-ui text-xs text-amber-800/70">
        {t('dashboard.planBanner.free.upgradeHint', {
          defaultValue: 'Upgrade to unlock all 4 modules, more attempts, and AI feedback.',
        })}
      </p>
    </div>
  );
}

// ── Starter banner ────────────────────────────────────────────────────────

function StarterBanner({ plan, t, locale, entitlement, onUpgrade }: BannerProps) {
  const modules = plan.limits.allowed_modules ?? [];
  const quota = plan.mock_quota ?? 3;
  const moduleUsage = plan.module_usage ?? {};

  const timeLabel = plan.expires_at ? formatTimeRemaining(plan.expires_at, locale) : null;
  const msLeft = plan.expires_at ? msUntilExpiry(plan.expires_at) : Infinity;
  const isUrgent = msLeft / (1000 * 60 * 60 * 24) < URGENCY_DAYS;
  const hasExhaustedModule = modules.some((m) => (moduleUsage[m] ?? 0) >= quota);

  const colorScheme = isUrgent
    ? {
        border: 'border-orange-400/50',
        bg: 'bg-orange-50/80',
        icon: 'bg-orange-400/20 text-orange-700',
        title: 'text-orange-900',
        sub: 'text-orange-800/70',
        badge: 'border-orange-400/60 text-orange-800 bg-orange-400/10',
        btn: 'bg-orange-600 hover:bg-orange-700 text-white',
      }
    : {
        border: 'border-sky-400/50',
        bg: 'bg-sky-50/80',
        icon: 'bg-sky-400/20 text-sky-700',
        title: 'text-sky-900',
        sub: 'text-sky-800/70',
        badge: 'border-sky-400/60 text-sky-800 bg-sky-400/10',
        btn: 'bg-sky-600 hover:bg-sky-700 text-white',
      };

  return (
    <div className={cn('rounded-lg border-2 p-4 space-y-3', colorScheme.border, colorScheme.bg)}>
      {/* Header row */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex gap-3 items-start">
          <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', colorScheme.icon)}>
            <Clock className="h-4 w-4" aria-hidden />
          </div>
          <div className="space-y-0.5">
            <div className="flex flex-wrap items-center gap-2">
              <p className={cn('font-ui font-semibold text-sm', colorScheme.title)}>
                {t('dashboard.planBanner.starter.title', { defaultValue: 'Starter Pass' })}
              </p>
              {timeLabel && (
                <Badge variant="outline" className={cn('font-ui text-xs', colorScheme.badge)}>
                  {timeLabel}
                </Badge>
              )}
            </div>
            {plan.chosen_level && (
              <p className={cn('font-ui text-xs', colorScheme.sub)}>
                {t('dashboard.planBanner.level', {
                  defaultValue: 'Level: {{level}}',
                  level: plan.chosen_level,
                })}
              </p>
            )}
          </div>
        </div>
        {(isUrgent || hasExhaustedModule) && (
          <Button
            type="button"
            size="sm"
            className={cn('font-ui shrink-0 border-0', colorScheme.btn)}
            onClick={onUpgrade}
          >
            {t('dashboard.planBanner.renewCta', { defaultValue: 'Renew plan' })}
          </Button>
        )}
      </div>

      {/* Per-module attempt cards */}
      <div className="space-y-1.5">
        <p className={cn('font-ui text-xs font-medium uppercase tracking-wide', colorScheme.sub)}>
          {t('dashboard.planBanner.moduleAttemptsTitle', { defaultValue: 'Attempts per module' })}
        </p>
        <div className="flex flex-wrap gap-2">
          {modules.map((module) => (
            <ModuleMiniCard
              key={module}
              module={module}
              used={moduleUsage[module] ?? 0}
              quota={quota}
              t={t}
            />
          ))}
        </div>
      </div>

      {entitlement && (
        <div className="space-y-1.5">
          <p className={cn('font-ui text-xs font-medium uppercase tracking-wide', colorScheme.sub)}>
            {t('dashboard.planBanner.humanReviewsTitle', { defaultValue: 'Human reviews' })}
          </p>
          <HumanReviewMiniCard entitlement={entitlement} t={t} />
        </div>
      )}
    </div>
  );
}

// ── Paid-unlimited banner (Exam-Ready / Full Prep) ────────────────────────

function PaidUnlimitedBanner({ plan, t, locale, entitlement, onUpgrade }: BannerProps) {
  const timeLabel = plan.expires_at ? formatTimeRemaining(plan.expires_at, locale) : null;
  const msLeft = plan.expires_at ? msUntilExpiry(plan.expires_at) : Infinity;
  const isUrgent = msLeft / (1000 * 60 * 60 * 24) < URGENCY_DAYS;

  const colorScheme = isUrgent
    ? {
        border: 'border-orange-400/50',
        bg: 'bg-orange-50/80',
        icon: 'bg-orange-400/20 text-orange-700',
        title: 'text-orange-900',
        sub: 'text-orange-800/70',
        badge: 'border-orange-400/60 text-orange-800 bg-orange-400/10',
        btn: 'bg-orange-600 hover:bg-orange-700 text-white',
      }
    : {
        border: 'border-emerald-400/50',
        bg: 'bg-emerald-50/80',
        icon: 'bg-emerald-400/20 text-emerald-700',
        title: 'text-emerald-900',
        sub: 'text-emerald-800/70',
        badge: 'border-emerald-400/60 text-emerald-800 bg-emerald-400/10',
        btn: 'bg-orange-600 hover:bg-orange-700 text-white',
      };

  const IconComponent = isUrgent ? Clock : CheckCircle2;

  return (
    <div className={cn('rounded-lg border-2 p-4 space-y-3', colorScheme.border, colorScheme.bg)}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex gap-3 items-center">
          <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', colorScheme.icon)}>
            <IconComponent className="h-4 w-4" aria-hidden />
          </div>
          <div className="space-y-0.5">
            <div className="flex flex-wrap items-center gap-2">
              <p className={cn('font-ui font-semibold text-sm', colorScheme.title)}>
                {plan.limits.display_name}
              </p>
              {timeLabel && (
                <Badge variant="outline" className={cn('font-ui text-xs', colorScheme.badge)}>
                  {timeLabel}
                </Badge>
              )}
            </div>
            <p className={cn('font-ui text-xs', colorScheme.sub)}>
              {plan.chosen_level
                ? t('dashboard.planBanner.unlimitedWithLevel', {
                    defaultValue: 'Unlimited mock exams · Level {{level}}',
                    level: plan.chosen_level,
                  })
                : t('dashboard.planBanner.unlimitedAllLevels', {
                    defaultValue: 'Unlimited mock exams · All levels & providers',
                  })}
            </p>
          </div>
        </div>
        {isUrgent && (
          <Button
            type="button"
            size="sm"
            className={cn('font-ui shrink-0 border-0', colorScheme.btn)}
            onClick={onUpgrade}
          >
            {t('dashboard.planBanner.renewCta', { defaultValue: 'Renew plan' })}
          </Button>
        )}
      </div>

      {entitlement && (
        <div className="space-y-1.5">
          <p className={cn('font-ui text-xs font-medium uppercase tracking-wide', colorScheme.sub)}>
            {t('dashboard.planBanner.humanReviewsTitle', { defaultValue: 'Human reviews' })}
          </p>
          <HumanReviewMiniCard entitlement={entitlement} t={t} />
        </div>
      )}
    </div>
  );
}

// ── Expired banner ────────────────────────────────────────────────────────

interface ExpiredBannerProps {
  readonly displayName: string;
  readonly expiredAt: string;
  readonly t: TFunction;
  readonly locale: string;
  readonly onUpgrade: () => void;
  readonly onDismiss: () => void;
}

function ExpiredBanner({
  displayName,
  expiredAt,
  t,
  locale,
  onUpgrade,
  onDismiss,
}: ExpiredBannerProps) {
  let dateLabel = '';
  try {
    dateLabel = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(
      new Date(expiredAt),
    );
  } catch {
    dateLabel = '';
  }

  return (
    <div className="relative rounded-lg border-2 border-destructive/40 bg-destructive/5 p-4">
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('dashboard.planBanner.expired.dismiss', { defaultValue: 'Dismiss' })}
        className="absolute right-2 top-2 rounded-md p-1 text-destructive/70 transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/40"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
      <div className="flex flex-col gap-3 pr-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex gap-3 items-start">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
            <AlertCircle className="h-4 w-4" aria-hidden />
          </div>
          <div>
            <p className="font-ui font-semibold text-sm text-destructive">
              {t('dashboard.planBanner.expired.title', { defaultValue: 'Pass expired' })}
            </p>
            <p className="font-ui text-xs text-destructive/80 mt-0.5">
              {dateLabel
                ? t('dashboard.planBanner.expired.descriptionDated', {
                    defaultValue:
                      'Your {{planName}} expired on {{date}}. Renew to start or resume simulations.',
                    planName: displayName,
                    date: dateLabel,
                  })
                : t('dashboard.planBanner.expired.description', {
                    defaultValue:
                      'Your {{planName}} has expired. Renew to start or resume simulations.',
                    planName: displayName,
                  })}
            </p>
          </div>
        </div>
        <Button
          type="button"
          size="sm"
          variant="destructive"
          className="font-ui shrink-0"
          onClick={onUpgrade}
        >
          {t('dashboard.planBanner.expired.cta', { defaultValue: 'Renew plan' })}
        </Button>
      </div>
    </div>
  );
}

// ── Public component ──────────────────────────────────────────────────────

/**
 * Renders the appropriate plan banner for the current user's tier.
 * Returns null while the plan is loading or unavailable.
 */
export function PlanBanner() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { plan, isLoading } = useCurrentPlan();
  const {
    entitlement,
    isLoading: isEntitlementLoading,
    isError: isEntitlementError,
  } = useEntitlement(!isLoading);

  const dismissKey = user?.id ? `${EXPIRY_DISMISS_KEY_PREFIX}${user.id}` : null;
  const [dismissedExpiry, setDismissedExpiry] = useState<string | null>(null);

  // Load this user's previously dismissed expiry timestamp.
  useEffect(() => {
    if (!dismissKey) {
      setDismissedExpiry(null);
      return;
    }
    try {
      setDismissedExpiry(localStorage.getItem(dismissKey));
    } catch {
      setDismissedExpiry(null);
    }
  }, [dismissKey]);

  const handleUpgrade = () => {
    navigate(`${ROUTES.SETTINGS}#plan`);
  };

  const handleDismissExpiry = (expiredAt: string) => {
    if (dismissKey) {
      try {
        localStorage.setItem(dismissKey, expiredAt);
      } catch {
        /* ignore storage errors — dismissal just won't persist */
      }
    }
    setDismissedExpiry(expiredAt);
  };

  if (isLoading || !plan) return null;

  const locale = i18n.language || 'en';

  // A recently expired pass takes precedence (shown until dismissed). The
  // backend only returns recent_expiry while the user is on free.
  const recentExpiry = plan.recent_expiry ?? null;
  if (recentExpiry && dismissedExpiry !== recentExpiry.expired_at) {
    return (
      <ExpiredBanner
        displayName={recentExpiry.display_name}
        expiredAt={recentExpiry.expired_at}
        t={t}
        locale={locale}
        onUpgrade={handleUpgrade}
        onDismiss={() => handleDismissExpiry(recentExpiry.expired_at)}
      />
    );
  }

  const props: BannerProps = {
    plan,
    t,
    locale,
    onUpgrade: handleUpgrade,
    entitlement: !isEntitlementLoading && !isEntitlementError ? entitlement : null,
  };

  switch (plan.plan_id) {
    case 'free':
      return <FreeBanner {...props} />;
    case 'starter':
      return <StarterBanner {...props} />;
    default:
      // exam-ready, full-prep
      return <PaidUnlimitedBanner {...props} />;
  }
}
